import { PageCard } from "@common/components/layout";
import { YesNoConfirmModal } from '@common/components/modals';
import { Pager } from "@common/components/pagination";
import { TableSkeleton } from "@common/components/skeleton/TableSkeleton";
import { IStoreListingModel } from "@common/models";
import { IStoreSearchFilter, defaultStoreSearchFilter } from "@common/stores/search";
import { AdminPage, Page } from "@hospital/components/layout";
import { useCreateLocationStore } from "@hospital/stores";
import { observer } from "mobx-react";
import React, { useEffect } from 'react';
import { useNavigate, useOutlet } from "react-router-dom";
import { LocationsFilter, LocationsTable, LocationsTableHeader } from ".";
import { FilterByTitle } from "@common/components/FilterByTitle";

export const LocationsContainer = observer(() => {
    const store = useCreateLocationStore({ shouldSetContext: true });
    const navigate = useNavigate();
    const outlet = useOutlet();

    //update search anytime searchParams changes and on initial run
    useEffect(() => {
        if (!outlet)
            store.searchStore.search();
    }, [store, store.searchStore.combinedSearchParams, outlet])

    if (outlet)
        return outlet;

    //if the pager changes, apply the new page in the store, which will trigger an update to searchParams computed which will trigger the useEffect to update the search results.
    const browseToEdit = (id: number) => navigate(`/admin/locations/${id}`);
    const onHospitalEdit = (loc: IStoreListingModel) => browseToEdit(loc.storeId);
    const onStoreConvert = (loc: IStoreListingModel) => store.setStoreToConvert(loc);

    function updateFilter(filter: IStoreSearchFilter) {
        if (!filter.isHospital)
            filter.isHospital = undefined;
        store.searchStore.updateSearchFilter(filter);
    }

    return (
        <>
            <YesNoConfirmModal
                visible={store.isConfirmCreateHospitalModalVisible}
                title="Create new hospital location"
                description={store.confirmCreateHospitalModalDescription}
                busy={store.storeIsBeingConverted}
                onConfirm={() => store.convertStoreToHospital()
                    .then(() => { if (store.hospital) { browseToEdit(store.hospital.hospitalId) } })}
                onCancel={store.clearStoreToConvert}
            />
            <AdminPage>
                <Page.PageTitle text="Locations"></Page.PageTitle>
                <Page.Content>
                    <PageCard>
                        {store.searchStore.isLoading &&
                            <TableSkeleton rows={10} cols={5}><LocationsTableHeader /></TableSkeleton>}
                        {!store.searchStore.isLoading && store.searchStore.searchResults.totalRecordCount === 0 &&
                            <p>No locations match that combination of filters.</p>}
                        {!store.searchStore.isLoading && store.searchStore.searchResults.totalRecordCount !== 0 &&
                            <LocationsTable
                                locations={store.searchStore.searchResults.results}
                                onHospitalEdit={onHospitalEdit}
                                onStoreConvert={onStoreConvert} />}
                        {store.searchStore.hasMultiplePages &&
                            <Pager
                                skip={store.searchStore.searchParams.skip ? store.searchStore.searchParams.skip : 0}
                                totalItems={store.searchStore.searchResults.totalRecordCount}
                                pageSize={store.searchStore.searchParams.top}
                                onPageChange={store.searchStore.changePage} />}
                    </PageCard>
                </Page.Content>
                <Page.SideBar title="Filter By">
                    <FilterByTitle />
                    <LocationsFilter
                        filter={store.searchStore.searchFilter}
                        defaultFilter={defaultStoreSearchFilter}
                        onApplyFilter={updateFilter}
                        onClearFilter={store.searchStore.resetSearchFilter} />
                </Page.SideBar>
            </AdminPage>
        </>
    )
});
